import {
    EzLogLevel
} from '/ezlibrary/enums/EzEnumerations.js';

/**
 * @class
 * @description
 * Implementation of a logging console
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *      import { EzDefaultLogConsole } from '/ezlibrary/logging/EzDefaultLogConsole.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzDefaultLogConsole {
    /**
     * @public @constructor
     * Creates a new instance of EzDefaultLogConsole
     *
     * @param {string|null} consoleName
     * @param {number|null} maxLength
     */
    constructor(consoleName, maxLength) {
        this.ezConsoleName = consoleName;
        this.ezMaxLength = maxLength;
    }

    /**
     * @private @field
     * Stores
     *
     * @type {number}
     */
    #ezMaxLength = 100;
    /**
     * @public @property @getter
     * Gets
     *
     * @returns {number}
     */
    get ezMaxLength() {
        return this.#ezMaxLength;
    }
    /**
     * @public @property @setter
     *
     * @param {number} maxLength
     * Default: 100
     */
    set ezMaxLength(maxLength = 100) {
        this.#ezMaxLength = undefined !== maxLength && null != maxLength && 'number' === typeof maxLength && 0 > maxLength
            ? maxLength
            : 100;
    }

    /**
     * @private @field
     * Stores
     *
     * @type {string}
     */
    #ezConsoleName = 'EzClockerConsole';
    /**
     * @public @property @getter
     * Gets
     *
     * @returns {string}
     */
    get ezConsoleName() {
        return this.#ezConsoleName;
    }
    /**
     * @public @property @setter
     * Sets
     *
     * @param {string} consoleName
     * Default: 'EzClockerConsole'
     */
    set ezConsoleName(consoleName = 'EzClockerConsole') {
        this.#ezConsoleName = undefined !== consoleName && null != consoleName && 'string' === typeof consoleName && 0 !== consoleName.length
            ? consoleName
            : 'EzClockerConsole';
    }

    /**
     * @private @field
     * Stores
     *
     * @type {array}
     */
    #ezLog = [];
    /**
     * @public @property @getter
     * Gets
     *
     * @returns {array}
     */
    get internalLog() {
        return this.#ezLog;
    }
    /**
     * @public @property @setter
     * Sets
     *
     * @param {array} logArray
     * Default: []
     */
    set internalLog(logArray = []) {
        this.ezLog = undefined !== logArray && null !== logArray && '[object Array]' === Object.prototype.toString.call(logArray)
            ? logArray
            : [];
    }

    /**
     * @public @method
     * Logs an error level message
     *
     * @param {string} msg
     */
    error(msg) {
        this.#ezLogAppend(msg, EzLogLevel.ERROR);
    }

    /**
     * @public @method
     * Logs an error level message
     *
     * @param {string} msg
     */
    warn(msg) {
        this.#ezLogAppend(msg, EzLogLevel.WARN);
    }

    /**
     * @public @method
     * Logs an error level message
     *
     * @param {string} msg
     */
    log(msg) {
        this.#ezLogAppend(msg, EzLogLevel.INFO);
    }

    /**
     * @public @method
     * Logs an error level message
     *
     * @param {string} msg
     */
    info(msg) {
        this.#ezLogAppend(msg, EzLogLevel.INFO);
    }

    /**
     * @public @method
     * Logs an error level message
     *
     * @param {string} msg
     */
    debug(msg) {
        this.#ezLogAppend(msg, EzLogLevel.DEBUG);
    }

    /**
     * @private @method
     * Appends a log message to the array storing log file data
     *
     * @param {string} msg
     * @param {string} ezLogLevel
     * A valid enumeration property value from EzLogLevel
     * @param {undefined|null|string|Date|moment} date
     * Default: `[${moment().format()}]`
     */
    #ezLogAppend(msg, ezLogLevel, date) {
        if (!msg || 'string' !== typeof msg || 0 === msg.length) {
            // nothing to log
            return;
        }

        if (undefined === date || null == date || 'string' !== typeof date || 0 === date.length) {
            date = `[${moment().format()}]`;
        }

        if (this.#ezMaxLength <= this.#ezLog.length) {
            this.#ezLog.shift();
        }

        this.#ezLog.push(`${date} ${EzLogLevel.ezValueOf(ezLogLevel)}: ${msg}`);
    }
}
